import BaseApi from '@/api/Base/BaseApi'
import {
  CreateModelDriftNodePayload,
  UpdateModelDriftNodePayload,
  ModelDriftNodeInfo,
  GetModelDriftNodeItemsPayload,
  GetModelDriftImageCountsResponse,
  GetModelDriftImagesResponse,
  GetModelDriftResultResponse,
  StartModelDriftPayload,
  GetModelDriftResultPayload,
  UpdateModelDriftSettingsPayload,
  UpdateModelDriftNotiSettingsPayload
} from '@/api/ModelDrift/types'
import { CommonListPayload } from '@/api/DatasetNode/types'
import { ExternalModelListForSemiAutoLabelingResponse, ModelVersionListResponse } from '@/api/ModelVersion/types'
import { omitNilOrEmptyStringFromObject } from '@/utils/common'

const ROOT = '/model-drift'
const defaultPaginationOptions = {
  itemsPerPage: 10,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class ModelDriftApi extends BaseApi {
  createModelDriftNode(payload: CreateModelDriftNodePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post<ModelDriftNodeInfo>(`/project/${projectId}/model-drift/create`, rest)
  }

  getModelDriftNodeDetail(modelDriftNodeId: string) {
    return this.agent.get<ModelDriftNodeInfo>(`${ROOT}/${modelDriftNodeId}`)
  }

  updateModelDriftNode(payload: UpdateModelDriftNodePayload) {
    const { modelDriftNodeId, ...rest } = payload
    return this.agent.put(`${ROOT}/${modelDriftNodeId}`, rest)
  }

  runModelDrift(payload: StartModelDriftPayload) {
    const { modelDriftNodeId, ...rest } = payload
    return this.agent.post(`${ROOT}/${modelDriftNodeId}/drift`, rest)
  }

  getModelDriftImages(payload: GetModelDriftNodeItemsPayload) {
    const { modelDriftNodeId, page, itemsPerPage, sortKey, sortDir, keyword, from, to, ...rest } = payload ?? defaultPaginationOptions
    const params = omitNilOrEmptyStringFromObject({
      page,
      num_per_page: itemsPerPage,
      sort_key: sortKey,
      sort_dir: sortDir,
      search: keyword,
      startDate: from,
      endDate: to,
      ...rest,
    })
    return this.agent.get<GetModelDriftImagesResponse>(`${ROOT}/${modelDriftNodeId}/item`, { params })
  }

  getModelDriftImageCounts(payload: GetModelDriftNodeItemsPayload) {
    const { modelDriftNodeId, from, to } = payload
    return this.agent.get<GetModelDriftImageCountsResponse>(`${ROOT}/${modelDriftNodeId}/item-count`, {
      params: {
        startDate: from,
        endDate: to,
      },
    })
  }

  changeModelDriftThreshold(modelDriftNodeId: string, modelDriftThreshold: number) {
    return this.agent.put(`${ROOT}/${modelDriftNodeId}/threshold`, { modelDriftThreshold })
  }

  getModelDriftResult(payload: GetModelDriftResultPayload) {
    const { modelDriftNodeId, page, from, to, itemsPerPage = 10, classId, sortKey, sortDir, keyword, hasAnomaly, ...rest } = payload ?? defaultPaginationOptions
    const params = omitNilOrEmptyStringFromObject({
      page,
      num_per_page: itemsPerPage,
      sort_key: sortKey,
      sort_dir: sortDir,
      search: keyword,
      startDate: from,
      endDate: to,
      classId,
      hasAnomaly,
      ...rest,
    })
    return this.agent.get<GetModelDriftResultResponse>(`${ROOT}/${modelDriftNodeId}/result`, { params })
  }

  getModelVersionList(modelDriftNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page } = options ?? defaultPaginationOptions

    return this.agent.get<ModelVersionListResponse>(`${ROOT}/${modelDriftNodeId}/model-versions`, {
      params: {
        page,
        num_per_page: itemsPerPage,
      },
    })
  }

  updateModelDriftSettings(payload: UpdateModelDriftSettingsPayload) {
    const { modelDriftNodeId, ...rest } = payload
    return this.agent.put(`${ROOT}/${modelDriftNodeId}/setting`, rest)
  }

  updateModelDriftNotiSettings(payload: UpdateModelDriftNotiSettingsPayload) {
    const { modelDriftNodeId, ...rest } = payload
    return this.agent.put(`${ROOT}/${modelDriftNodeId}/notification-settings`, rest)
  }

  getSemiAutoModelVersionList(modelDriftNodeId: string, options?: any) {
    const { modelSize = 'LARGE', page, itemsPerPage = 10 } = options ?? {}
    const params = omitNilOrEmptyStringFromObject({
      page,
      num_per_page: itemsPerPage,
      model_size: modelSize,
    })
    return this.agent.get<ModelVersionListResponse>(`${ROOT}/${modelDriftNodeId}/semi-auto-list`, { params })
  }

  getExternalModelListForSemiAutoLabeling(modelDriftNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page } = options ?? defaultPaginationOptions

    return this.agent.get<ExternalModelListForSemiAutoLabelingResponse>(`${ROOT}/${modelDriftNodeId}/external-models`, {
      params: {
        page,
        num_per_page: itemsPerPage,
      },
    })
  }
}
